import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  ShieldCheck,
  AlertCircle,
  MessageCircle,
  GraduationCap,
  Clock3,
  Calendar,
  Lock,
  ArrowRight,
  FlaskConical,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getProvaPublica, submeterProva, validarInscricaoProva } from "@/lib/bolsa-feo/prova.functions";

import { formatCpf, onlyDigits } from "@/lib/bolsa-feo/cpf";
import { getProvaEncerramentoDate, getProvaLiberacaoDate } from "@/lib/bolsa-feo/config";
import logoFeo from "@/assets/feo/logo-feo.png";

const PROVA_DURATION_SECONDS = 2 * 60 * 60;


export const Route = createFileRoute("/bolsas-feo-prova")({
  head: () => ({
    meta: [
      { title: "Prova · Programa Bolsas FEO 2026" },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content:
          "Prova online do Programa Bolsas FEO 2026, acesso exclusivo para inscritos, identificação por CPF.",
      },
      { property: "og:title", content: "Prova · Programa Bolsas FEO 2026" },
      {
        property: "og:description",
        content: "Acesso exclusivo para inscritos no Programa Bolsas FEO 2026.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ProvaPage,
});

type Inscrito = {
  id: string;
  nome: string;
  email_masked: string;
  telefone_masked: string;
  cpf: string; // CPF digitado pelo próprio candidato (não vem do servidor)
  is_test: boolean;
};

type Question = { id: number; enunciado: string; alternativas: { key: string; text: string }[] };

const CRONOGRAMA = [
  { data: "23/06", titulo: "Inscrições abertas", status: "feito" as const },
  { data: "02/07", titulo: "Inscrições encerradas", status: "feito" as const },
  { data: "05/07", titulo: "Prova online (esta página)", status: "prova" as const },
  { data: "14/07", titulo: "Resultados no WhatsApp + e-mail", status: "futuro" as const },
];


function ProvaPage() {
  const validar = useServerFn(validarInscricaoProva);
  const submeter = useServerFn(submeterProva);

  const provaQ = useQuery({
    queryKey: ["bolsa-feo-prova"],
    queryFn: () => getProvaPublica(),
    staleTime: 60_000,
  });

  const [stage, setStage] = useState<"intro" | "prova" | "enviado">("intro");
  const [cpfInput, setCpfInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [provaToken, setProvaToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inscrito, setInscrito] = useState<Inscrito | null>(null);
  const [confirmPhone, setConfirmPhone] = useState("");
  const [respostas, setRespostas] = useState<Record<number, string>>({});
  const startedAtRef = useRef<number | null>(null);
  const [deadlineAt, setDeadlineAt] = useState<number | null>(null);

  // Restaurar progresso local (mesmo CPF + ainda não enviado).
  // Inscritos de TESTE não usam progresso salvo (cada execução é uma rodada nova).
  useEffect(() => {
    if (stage !== "prova" || !inscrito) return;
    if (inscrito.is_test) {
      setRespostas({});
      return;
    }
    const key = `bolsa-feo:respostas:${onlyDigits(inscrito.cpf)}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const obj = JSON.parse(raw) as Record<number, string>;
        if (obj && typeof obj === "object") setRespostas(obj);
      }
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, inscrito?.id]);

  useEffect(() => {
    if (stage !== "prova" || !inscrito || inscrito.is_test) return;
    try {
      localStorage.setItem(
        `bolsa-feo:respostas:${onlyDigits(inscrito.cpf)}`,
        JSON.stringify(respostas),
      );
    } catch {
      /* noop */
    }
  }, [respostas, inscrito, stage]);

  useEffect(() => {
    if (stage !== "prova") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [stage]);

  const handleValidar = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = onlyDigits(cpfInput);
    if (digits.length !== 11) return toast.error("Informe os 11 dígitos do CPF.");
    const phone = onlyDigits(phoneInput);
    if (phone.length < 10)
      return toast.error("Informe o número completo do WhatsApp (com DDD) usado na inscrição.");
    setBusy(true);
    try {
      const res = await validar({ data: { cpf: digits, telefone: phone } });
      if (!res.ok) {
        toast.error(res.message);
        return;
      }
      setProvaToken(res.token);
      setInscrito({
        ...res.inscrito,
        // CPF é o que o próprio candidato digitou — servidor não devolve mais.
        cpf: formatCpf(digits),
      });

      // Sincroniza o cronômetro com o servidor (fonte da verdade).
      startedAtRef.current = res.started_at;
      setDeadlineAt(res.deadline_at);

      setConfirmPhone("");

      setStage("prova");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao validar inscrição.");
    } finally {
      setBusy(false);
    }
  };


  const total = provaQ.data?.total ?? 0;
  const respondidas = Object.keys(respostas).length;
  const podeEnviar = total > 0 && respondidas === total && !!inscrito;

  const handleEnviar = async () => {
    if (!inscrito || !provaQ.data) return;
    const phone = onlyDigits(confirmPhone);
    if (phone.length < 10) return toast.error("Confirme seu WhatsApp com DDD para o time entrar em contato.");
    if (!podeEnviar) return toast.error("Responda todas as questões antes de enviar.");

    const ok = window.confirm(
      inscrito.is_test
        ? "Modo TESTE, sua submissão será marcada como teste no banco. Continuar?"
        : "Tem certeza que deseja enviar sua prova? Você só pode enviar uma única vez.",
    );
    if (!ok) return;

    setBusy(true);
    try {
      const duration = startedAtRef.current
        ? Math.round((Date.now() - startedAtRef.current) / 1000)
        : undefined;
      if (!provaToken) {
        toast.error("Sua sessão expirou. Recarregue a página e identifique-se novamente.");
        return;
      }
      await submeter({
        data: {
          token: provaToken,
          duration_seconds: duration,
          respostas: provaQ.data.questions.map((q) => ({
            id: q.id,
            resposta: respostas[q.id] as "a" | "b" | "c" | "d" | "e",
          })),
        },
      });

      try {
        const k = onlyDigits(inscrito.cpf);
        localStorage.removeItem(`bolsa-feo:respostas:${k}`);
        localStorage.removeItem(`bolsa-feo:start:${k}`);
      } catch {
        /* noop */
      }
      setStage("enviado");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar prova.");
    } finally {
      setBusy(false);
    }
  };

  const showTimer = stage === "prova" && !!inscrito && deadlineAt != null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0a1f] via-[#13123a] to-[#1a1850] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b0a1f]/85 backdrop-blur supports-[backdrop-filter]:bg-[#0b0a1f]/70">
        <div className="mx-auto max-w-4xl px-3 sm:px-4 py-3 flex items-center gap-3">
          <img decoding="async" src={logoFeo} alt="FEO" className="h-8 sm:h-9 w-auto" />
          <div className="h-7 w-px bg-white/15" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 truncate">
              Programa Bolsas FEO 2026
            </div>
            <div className="text-sm font-semibold truncate">Prova Online · 05/07</div>
          </div>
          {showTimer && (
            <CountdownBadge
              deadlineAt={deadlineAt!}
              isTest={!!inscrito?.is_test}
            />
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-3 sm:px-4 py-6 sm:py-12">
        {stage === "intro" && (
          <IntroCard
            cpf={cpfInput}
            onChangeCpf={setCpfInput}
            phone={phoneInput}
            onChangePhone={setPhoneInput}
            onSubmit={handleValidar}
            busy={busy}
            liberacaoAt={getProvaLiberacaoDate().getTime()}
            encerramentoAt={getProvaEncerramentoDate().getTime()}
          />
        )}

        {stage === "prova" && inscrito && provaQ.data && (
          <ProvaForm
            inscrito={inscrito}
            questions={provaQ.data.questions}
            respostas={respostas}
            onChange={setRespostas}
            confirmPhone={confirmPhone}
            onPhoneChange={setConfirmPhone}
            onSubmit={handleEnviar}
            busy={busy}
            podeEnviar={podeEnviar}
          />
        )}

        {stage === "enviado" && <EnviadoCard nome={inscrito?.nome ?? null} isTest={!!inscrito?.is_test} />}
      </main>

      <footer className="border-t border-white/10 py-6 px-4 text-center text-xs text-white/40">
        <div>Programa Bolsas FEO 2026 · Fisio em Ortopedia</div>
      </footer>
    </div>
  );

}

function IntroCard({
  cpf,
  onChangeCpf,
  phone,
  onChangePhone,
  onSubmit,
  busy,
  liberacaoAt,
  encerramentoAt,
}: {
  cpf: string;
  onChangeCpf: (v: string) => void;
  phone: string;
  onChangePhone: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;
  liberacaoAt: number;
  encerramentoAt: number;
}) {
  // Inicializa como null para não renderizar valor volátil no SSR (evita hydration mismatch).
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Fallback SSR: assume estado "pré-liberação" enquanto o cliente não hidratou.
  const currentNow = now ?? liberacaoAt - 1;
  const liberada = currentNow >= liberacaoAt;
  const encerrada = currentNow >= encerramentoAt;
  const emAndamento = liberada && !encerrada;
  const remaining = Math.max(0, liberacaoAt - currentNow);
  const podeIniciar = emAndamento;

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#f5b022]/15 border border-[#f5b022]/40 px-3 py-1 text-xs font-semibold text-[#f5b022]">
          <GraduationCap className="h-3.5 w-3.5" /> Prova exclusiva para inscritos
        </div>
        <h1 className="mt-4 text-2xl sm:text-3xl font-bold leading-tight">
          Programa Bolsas FEO 2026 · Prova Online
        </h1>
        <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed">
          Bem-vindo(a)! Esta é a etapa de prova do programa de 10 bolsas integrais (100%) do Fisio em
          Ortopedia. A prova só pode ser realizada por candidatos previamente inscritos e <strong>uma única vez</strong>,
          identificada pelo <strong>CPF</strong> usado na inscrição.
        </p>
      </div>

      {/* Cronograma */}
      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#f5b022]" /> Cronograma oficial
        </h2>
        <ul className="mt-4 space-y-2">
          {CRONOGRAMA.map((c) => {
            // Estado da etapa "prova" depende do horário atual.
            const provaState: "em-breve" | "hoje" | "encerrada" =
              c.status === "prova"
                ? encerrada
                  ? "encerrada"
                  : emAndamento
                    ? "hoje"
                    : "em-breve"
                : "em-breve";

            const color =
              c.status === "feito"
                ? "border-emerald-500/30 bg-emerald-500/5"
                : c.status === "prova"
                  ? provaState === "hoje"
                    ? "border-[#f5b022]/40 bg-[#f5b022]/10"
                    : provaState === "encerrada"
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-white/10 bg-white/[0.02]"
                  : "border-white/10 bg-white/[0.02]";

            return (
              <li key={c.data} className={`flex items-center gap-3 rounded-xl border p-3 ${color}`}>
                <div className="font-mono text-sm font-bold tabular-nums w-14 shrink-0">{c.data}</div>
                <div className="text-sm flex-1">{c.titulo}</div>
                {c.status === "feito" && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                )}
                {c.status === "prova" && provaState === "hoje" && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#f5b022] shrink-0">
                    hoje
                  </span>
                )}
                {c.status === "prova" && provaState === "em-breve" && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-white/50 shrink-0">
                    em breve
                  </span>
                )}
                {c.status === "prova" && provaState === "encerrada" && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Como funciona */}
      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#f5b022]" /> Como funciona
        </h2>
        <ol className="mt-4 space-y-2 text-sm text-white/80 leading-relaxed">
          <li>• 25 questões objetivas (a-e), todas obrigatórias.</li>
          <li>
            • Você tem <strong>2 horas</strong> para concluir a prova, contadas a partir do momento em
            que você inicia. Um cronômetro fica fixo no topo da tela.
          </li>
          <li>• Responda com calma, você pode revisar antes de enviar.</li>
          <li>
            • Após o envio, a prova é registrada e <strong>não pode ser refeita</strong>. Nota e gabarito
            não são exibidos.
          </li>
          <li>
            • Os resultados serão divulgados em <strong>14/07</strong>, principalmente pelo{" "}
            <strong>WhatsApp do nosso time</strong>, e também por <strong>e-mail</strong> no mesmo dia
            com o link da página oficial de resultados.
          </li>
        </ol>
      </div>

      {/* Acesso */}
      <form
        onSubmit={(e) => {
          onSubmit(e);
        }}
        className="rounded-2xl border border-[#f5b022]/40 bg-[#f5b022]/5 backdrop-blur p-6 sm:p-8 space-y-4"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/80">
          <Lock className="h-3 w-3" /> Acesso restrito a inscritos
        </div>
        <h2 className="text-xl sm:text-2xl font-bold leading-tight">Identifique-se pelo CPF</h2>
        <p className="text-sm text-white/70">
          Digite o CPF cadastrado na sua inscrição. Ele será usado para vincular sua prova ao seu
          cadastro e garantir que ninguém faça a prova mais de uma vez.
        </p>

        {encerrada && (
          <div className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-xs sm:text-sm text-white/80 flex items-start gap-3">
            <Lock className="h-4 w-4 text-white/60 shrink-0 mt-0.5" />
            <div className="flex-1 leading-relaxed">
              As provas foram <strong>encerradas às 17h00</strong> (horário de Brasília) do dia
              <strong> 05/07/2026</strong>. Não é mais possível iniciar a prova. Em caso de dúvida, fale
              com nosso time pelo WhatsApp.
            </div>
          </div>
        )}

        {!liberada && (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-xs sm:text-sm text-white/70 flex items-start gap-3">
            <Clock3 className="h-4 w-4 text-[#f5b022] shrink-0 mt-0.5" />
            <div className="flex-1 leading-relaxed">
              <strong>A prova será liberada em 05/07/2026, às 08h00 (horário de Brasília).</strong>
              {now !== null && remaining > 0 && (
                <div className="mt-0.5">Faltam <span className="tabular-nums">{formatCountdownText(remaining)}</span>.</div>
              )}
            </div>
          </div>
        )}

        <div>
          <Label className="text-white/70">CPF</Label>
          <Input
            inputMode="numeric"
            autoComplete="off"
            required
            disabled={busy || !podeIniciar}
            value={cpf}
            onChange={(e) => {
              const d = onlyDigits(e.target.value).slice(0, 11);
              onChangeCpf(formatCpf(d));
            }}
            placeholder="000.000.000-00"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 text-lg tracking-wider disabled:opacity-60 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <Label className="text-white/70">WhatsApp usado na inscrição (com DDD)</Label>
          <Input
            inputMode="numeric"
            autoComplete="off"
            required
            disabled={busy || !podeIniciar}
            value={phone}
            onChange={(e) => onChangePhone(onlyDigits(e.target.value).slice(0, 13))}
            placeholder="11999998888"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 text-lg tracking-wider disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-white/50">
            Confirmação de identidade: digite o número completo do WhatsApp que você informou na inscrição.
          </p>
        </div>

        <Button
          type="submit"
          disabled={busy || !podeIniciar}
          className="w-full bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold h-12 text-base disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
          Iniciar minha prova
        </Button>

        <WhatsAppSupportButtons />
      </form>
    </div>
  );
}

function WhatsAppSupportButtons() {
  const message = encodeURIComponent(
    "Olá! Estou na página da Prova Online do Programa Bolsas FEO 2026 e preciso de ajuda."
  );
  const contacts = [
    { label: "Contado 1 - Lucas", phone: "5511985788889" },
    { label: "Contato 2 - Joice", phone: "5511971810796" },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 space-y-3">
      <div className="flex items-center gap-2 text-xs text-white/60">
        <MessageCircle className="h-4 w-4 text-emerald-400" />
        <span>Alguma dúvida ou problema? Fale com a gente no WhatsApp:</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {contacts.map((c) => (
          <a
            key={c.phone}
            href={`https://wa.me/${c.phone}?text=${message}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            {c.label}
          </a>
        ))}
      </div>
    </div>
  );
}


// Formato compacto (usado no cronômetro em execução): "HH:MM:SS" ou "Dd HH:MM:SS".
function formatCountdown(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

// Formato textual para pré-liberação: "X dias, Y horas e Z minutos" (sem segundos).
function formatCountdownText(ms: number) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d} ${d === 1 ? "dia" : "dias"}`);
  if (h > 0 || d > 0) parts.push(`${h} ${h === 1 ? "hora" : "horas"}`);
  parts.push(`${m} ${m === 1 ? "minuto" : "minutos"}`);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} e ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`;
}

function ProvaForm({
  inscrito,
  questions,
  respostas,
  onChange,
  confirmPhone,
  onPhoneChange,
  onSubmit,
  busy,
  podeEnviar,
}: {
  inscrito: Inscrito;
  questions: Question[];
  respostas: Record<number, string>;
  onChange: (v: Record<number, string>) => void;
  confirmPhone: string;
  onPhoneChange: (v: string) => void;
  onSubmit: () => void;
  busy: boolean;
  podeEnviar: boolean;
}) {
  const respondidas = Object.keys(respostas).length;
  const total = questions.length;
  const progress = useMemo(
    () => Math.round((respondidas / Math.max(total, 1)) * 100),
    [respondidas, total],
  );

  const setResp = (qid: number, alt: string) => onChange({ ...respostas, [qid]: alt });

  return (
    <div className="space-y-6">
      {inscrito.is_test && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 flex items-start gap-2 text-xs text-amber-200">
          <FlaskConical className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>Modo TESTE</strong> ativo, esta inscrição pode realizar a prova várias vezes. Todos
            os envios serão marcados como <code className="bg-black/30 px-1 rounded">is_test=true</code> no
            banco e não contam para o resultado.
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Candidato(a)</div>
            <div className="text-lg font-bold truncate">{inscrito.nome}</div>
            <div className="text-xs text-white/60 truncate">CPF {formatCpf(inscrito.cpf)} · {inscrito.email_masked}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Progresso</div>
            <div className="text-xl font-bold tabular-nums">
              {respondidas}/{total}
            </div>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#f5b022] transition-all" style={{ width: `${progress}%` }} />
        </div>

        <div className="mt-4">
          <Label className="text-white/70 text-xs">Confirme seu WhatsApp para contato do time</Label>
          <Input
            value={confirmPhone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="(DDD) 9 0000-0000"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      {questions.map((q, idx) => (
        <div key={q.id} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#f5b022]">
            <span className="rounded-md bg-[#f5b022]/15 px-2 py-0.5">Questão {idx + 1}</span>
            {respostas[q.id] && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          </div>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed">{q.enunciado}</p>
          <div className="mt-4 space-y-2">
            {q.alternativas.map((alt) => {
              const selected = respostas[q.id] === alt.key;
              return (
                <label
                  key={alt.key}
                  className={`flex gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                    selected
                      ? "border-[#f5b022] bg-[#f5b022]/10"
                      : "border-white/10 bg-white/0 hover:bg-white/5"
                  }`}
                >
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    className="mt-1 accent-[#f5b022]"
                    checked={selected}
                    onChange={() => setResp(q.id, alt.key)}
                  />
                  <div className="flex-1 text-sm leading-relaxed">
                    <span className="font-semibold mr-2 uppercase">{alt.key})</span>
                    {alt.text}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
        {!podeEnviar && (
          <div className="mb-3 flex items-start gap-2 text-xs text-amber-300">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Responda todas as {total} questões antes de enviar.</span>
          </div>
        )}
        <Button
          onClick={onSubmit}
          disabled={!podeEnviar || busy}
          className="w-full bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold h-12 text-base"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          Enviar prova ({respondidas}/{total})
        </Button>
        <p className="mt-3 text-[11px] text-white/50 text-center">
          {inscrito.is_test
            ? "Modo teste: você pode enviar quantas vezes quiser."
            : "Após enviar, a prova é registrada e não pode ser refeita."}
        </p>
      </div>
    </div>
  );
}

function EnviadoCard({ nome, isTest }: { nome: string | null; isTest: boolean }) {
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 backdrop-blur p-6 sm:p-8 text-center">
      <div className="mx-auto inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-4">
        <CheckCircle2 className="h-8 w-8 text-emerald-400" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold">
        {isTest ? "Submissão de teste registrada!" : "Prova enviada com sucesso!"}
      </h1>
      <p className="mt-3 text-white/70 max-w-xl mx-auto">
        {isTest
          ? "Sua submissão foi salva no banco com a flag de teste. Você pode recarregar e refazer a prova com este CPF."
          : `${nome ? `${nome}, sua` : "Sua"} prova foi registrada e está em análise pelo time avaliador da FEO. Você não vai ver sua nota agora, esse processo é confidencial.`}
      </p>

      <div className="mt-6 mx-auto max-w-md rounded-xl border border-white/10 bg-white/5 p-5 text-left">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#f5b022]">
          <Clock3 className="h-4 w-4" /> Próximos passos
        </div>
        <ul className="mt-3 space-y-2 text-sm text-white/80">
          <li>• <strong>14/07</strong>: divulgação dos resultados.</li>
          <li>
            • A comunicação será feita <strong>principalmente pelo WhatsApp</strong>, com contato
            direto do <strong>nosso time</strong> no número que você cadastrou.
          </li>
          <li>
            • No mesmo dia, você também recebe um <strong>e-mail</strong> com o link da{" "}
            <strong>página oficial de resultados</strong>.
          </li>
          <li>• Não envie a prova por outro canal, seu envio já está registrado.</li>
        </ul>

        <div className="mt-4 text-center">
          <Link
            to="/bolsas-feo-resultados"
            className="inline-flex items-center gap-1 text-xs text-[#f5b022] hover:underline"
          >
            ver página oficial de resultados <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <div className="mt-6 inline-flex items-center gap-2 text-xs text-white/50">
        <MessageCircle className="h-3.5 w-3.5" />
        Dúvidas? Aguarde o contato do nosso time pelo WhatsApp.
      </div>
    </div>
  );
}

function CountdownBadge({ deadlineAt, isTest }: { deadlineAt: number; isTest: boolean }) {
  const [remaining, setRemaining] = useState<number>(() =>
    Math.max(0, Math.floor((deadlineAt - Date.now()) / 1000)),
  );
  const warned10Ref = useRef(false);
  const warnedEndRef = useRef(false);

  useEffect(() => {
    const tick = () => {
      const r = Math.max(0, Math.floor((deadlineAt - Date.now()) / 1000));
      setRemaining(r);
      if (!isTest && !warned10Ref.current && r > 0 && r <= 10 * 60) {
        warned10Ref.current = true;
        toast.warning("Faltam 10 minutos para o encerramento da prova.", {
          description: "Revise suas respostas e envie antes do tempo esgotar.",
          duration: 10_000,
        });
      }
      if (!isTest && !warnedEndRef.current && r === 0) {
        warnedEndRef.current = true;
        toast.error("Tempo esgotado.", {
          description:
            "O limite de 2h para a prova acabou. Envie agora, o servidor validará o envio.",
          duration: 15_000,
        });
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [deadlineAt, isTest]);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const label = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;

  const critical = remaining <= 10 * 60;
  const ended = remaining === 0;

  const color = ended
    ? "border-red-500/60 bg-red-500/15 text-red-300"
    : critical
      ? "border-amber-400/60 bg-amber-400/15 text-amber-200"
      : "border-white/15 bg-white/10 text-white/90";

  return (
    <div
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 sm:px-3 py-1 sm:py-1.5 font-mono tabular-nums text-xs sm:text-sm font-bold ${color}`}
      aria-live="polite"
      title={isTest ? "Modo teste · cronômetro apenas ilustrativo" : "Tempo restante da prova"}
    >
      <Clock3 className={`h-3.5 w-3.5 ${critical && !ended ? "animate-pulse" : ""}`} />
      <span className="hidden sm:inline text-[10px] uppercase tracking-wider font-semibold opacity-80 mr-0.5">
        {ended ? "Esgotado" : "Restante"}
      </span>
      <span>{label}</span>
    </div>
  );
}

